import { useEffect, useRef, useState, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { vehicleModelPageRouteName } from "../list/index.page";
import { parseFormattedNumber } from "_helper/formatters";
import {
  useVehicleModelServiceV1ListTollClassesQuery,
  useVehicleModelServiceV1BatchCreateVehicleModelMutation,
  useVehicleModelServiceV1GetVehicleTypeDropdownQuery,
  useVehicleModelServiceV1GetFreezerModelDropdownQuery,
  useVehicleModelServiceV1GetFuelTypeDropdownQuery,
} from "_services/modules/vehicle-model";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface VehicleModelItem {
  vehicleModel: string;
  vehicleCategory: string;
  vehicleType: string;
  vehicleTypeId: string;
  freezerModel: string;
  freezerModelId: string;
  tyreCount: number;
  tyreSize: string;
  axleCount: number;
  tollClass: string;
  tollClassId: string;
  fuelType: string;
  fuelTypeId: string;
  fuelCapacity: number | null;
  status: string;
}

export interface AddVehicleModelFormValues {
  vehicleModel: string;
  vehicleCategory: string;
  vehicleType: string;
  vehicleTypeId: string;
  vehicleTypeIsFreezer: boolean;
  freezerModel: string;
  freezerModelId: string;
  tyreCount: string;
  tyreSize: string;
  axleCount: string;
  tollClass: string;
  tollClassId: string;
  fuelType: string;
  fuelTypeId: string;
  fuelCapacity: string;
  status: string;
  items: VehicleModelItem[];
}



const useAddVehicleModelForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleModel");

  const schema = useMemo(() => yup.object({
    vehicleModel: yup.string().required().max(100),
    vehicleCategory: yup.string().oneOf(["truck", "chassis"]).required(),
    vehicleType: yup.string().required(),
    vehicleTypeId: yup.string().required(),
    vehicleTypeIsFreezer: yup.boolean().default(false),
    freezerModel: yup.string().when(["vehicleCategory", "vehicleTypeIsFreezer"], {
      is: (category: string, isFreezer: boolean) =>
        category === "truck" && isFreezer === true,
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    freezerModelId: yup.string().when(["vehicleCategory", "vehicleTypeIsFreezer"], {
      is: (category: string, isFreezer: boolean) =>
        category === "truck" && isFreezer === true,
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    tyreCount: yup
      .number()
      .typeError(t("error-type-number"))
      .required()
      .min(1),
    tyreSize: yup.string().notRequired(),
    axleCount: yup
      .number()
      .typeError(t("error-type-number"))
      .required()
      .min(1),
    tollClass: yup.string().required(),
    tollClassId: yup.string().required(),
    fuelType: yup.string().when("vehicleCategory", {
      is: "truck",
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired().default(""),
    }),
    fuelCapacity: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === "") return null;
        if (typeof originalValue === "string") return parseFormattedNumber(originalValue);
        return value;
      })
      .typeError(t("error-type-number"))
      .nullable()
      .min(0)
      .notRequired(),
    status: yup.string().required(),
    items: yup.array().of(
      yup.object({
        vehicleModel: yup.string().required(),
        vehicleCategory: yup.string().required(),
        vehicleType: yup.string().required(),
        vehicleTypeId: yup.string().required(),
        freezerModel: yup.string().default(""),
        freezerModelId: yup.string().default(""),
        tyreCount: yup.number().required().min(1),
        tyreSize: yup.string().notRequired(),
        axleCount: yup.number().required().min(1),
        tollClass: yup.string().required(),
        tollClassId: yup.string().required(),
        fuelType: yup.string().default(""),
        fuelTypeId: yup.string().default(""),
        fuelCapacity: yup.number().nullable().notRequired(),
        status: yup.string().required(),
      })
    ).required(),
  }).required(), [t]);

  const { data: tollClassesData } = useVehicleModelServiceV1ListTollClassesQuery();
  const { data: vehicleTypesData } =
    useVehicleModelServiceV1GetVehicleTypeDropdownQuery();
  const { data: freezerModelsData } =
    useVehicleModelServiceV1GetFreezerModelDropdownQuery();
  const { data: fuelTypesData } =
    useVehicleModelServiceV1GetFuelTypeDropdownQuery();
  const [batchCreateVehicleModel] = useVehicleModelServiceV1BatchCreateVehicleModelMutation();

  const tollClassOptions = useMemo(() => {
    if (!tollClassesData?.toll_classes) return [];
    return tollClassesData.toll_classes.map((tc) => ({
      label: tc.name || "",
      value: tc.name || "",
      id: tc.id || "",
    }));
  }, [tollClassesData]);

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddVehicleModelFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      vehicleModel: "",
      vehicleCategory: "truck",
      vehicleType: "",
      vehicleTypeId: "",
      vehicleTypeIsFreezer: false,
      freezerModel: "",
      freezerModelId: "",
      tyreCount: "",
      tyreSize: "",
      axleCount: "",
      tollClass: "",
      tollClassId: "",
      fuelType: "",
      fuelTypeId: "",
      fuelCapacity: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const watchedCategory = watch("vehicleCategory");
  const watchedVehicleType = watch("vehicleType");
  const watchedTollClass = watch("tollClass");
  const watchedFreezerModel = watch("freezerModel");
  const watchedFuelType = watch("fuelType");
  const prevCategory = useRef(watchedCategory);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  // Clear conditional fields when category changes
  useEffect(() => {
    if (prevCategory.current !== watchedCategory) {
      setValue("vehicleType", "");
      setValue("vehicleTypeId", "");
      setValue("freezerModel", "");
      setValue("freezerModelId", "");
      setValue("fuelType", "");
      setValue("fuelCapacity", "");
      prevCategory.current = watchedCategory;
    }
  }, [watchedCategory, setValue]);

  // Sync vehicleTypeIsFreezer flag and clear freezer fields when type changes
  useEffect(() => {
    const match = vehicleTypesData?.vehicle_types?.find(
      (vt) => vt.name === watchedVehicleType,
    );
    const isFreezer = match?.is_freezer ?? false;
    setValue("vehicleTypeIsFreezer", isFreezer);
    if (!isFreezer) {
      setValue("freezerModel", "");
      setValue("freezerModelId", "");
    }
  }, [watchedVehicleType, vehicleTypesData, setValue]);

  const isTruck = watchedCategory === "truck";

  const vehicleTypeOptions = useMemo(() => {
    if (!vehicleTypesData?.vehicle_types) return [];
    return vehicleTypesData.vehicle_types
      .filter(
        (vt) =>
          vt.category?.toLowerCase() === watchedCategory.toLowerCase(),
      )
      .map((vt) => ({
        label: vt.name || "",
        value: vt.name || "",
        id: vt.id || "",
      }));
  }, [vehicleTypesData, watchedCategory]);

  const freezerModelOptions = useMemo(() => {
    if (!freezerModelsData?.freezer_models) return [];
    return freezerModelsData.freezer_models.map((fm) => ({
      label: fm.name || "",
      value: fm.name || "",
      id: fm.id || "",
    }));
  }, [freezerModelsData]);

  const fuelTypeOptions = useMemo(() => {
    if (!fuelTypesData?.fuel_types) return [];
    return fuelTypesData.fuel_types.map((ft) => ({
      label: ft.fuel_type_name || "",
      value: ft.fuel_type_name || "",
      id: ft.id || "",
    }));
  }, [fuelTypesData]);

  const selectedVehicleTypeData = vehicleTypesData?.vehicle_types?.find(
    (vt) => vt.name === watchedVehicleType,
  );
  const isFreezerTruck = isTruck && (selectedVehicleTypeData?.is_freezer ?? false);

  // Sync vehicleTypeId whenever the selected name or options change
  useEffect(() => {
    if (!watchedVehicleType) {
      setValue("vehicleTypeId", "");
      return;
    }
    const match = vehicleTypeOptions.find(
      (opt) => opt.value === watchedVehicleType,
    );
    setValue("vehicleTypeId", match?.id ?? "");
  }, [watchedVehicleType, vehicleTypeOptions, setValue]);

  // Sync tollClassId whenever the selected toll class name changes
  useEffect(() => {
    if (!watchedTollClass) {
      setValue("tollClassId", "");
      return;
    }
    const match = tollClassOptions.find(
      (opt) => opt.value === watchedTollClass,
    );
    setValue("tollClassId", match?.id ?? "");
  }, [watchedTollClass, tollClassOptions, setValue]);

  // Sync freezerModelId whenever the selected freezer model name changes
  useEffect(() => {
    if (!watchedFreezerModel) {
      setValue("freezerModelId", "");
      return;
    }
    const match = freezerModelOptions.find(
      (opt) => opt.value === watchedFreezerModel,
    );
    setValue("freezerModelId", match?.id ?? "");
  }, [watchedFreezerModel, freezerModelOptions, setValue]);

  // Sync fuelTypeId whenever the selected fuel type name changes
  useEffect(() => {
    if (!watchedFuelType) {
      setValue("fuelTypeId", "");
      return;
    }
    const match = fuelTypeOptions.find(
      (opt) => opt.value === watchedFuelType,
    );
    setValue("fuelTypeId", match?.id ?? "");
  }, [watchedFuelType, fuelTypeOptions, setValue]);

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddVehicleModelFormValues)[] = [
      "vehicleModel",
      "vehicleCategory",
      "vehicleType",
      "vehicleTypeId",
      "tyreCount",
      "axleCount",
      "tollClass",
      "tollClassId",
      "status",
    ];
    if (isTruck) fieldsToValidate.push("fuelType");
    if (isFreezerTruck) {
      fieldsToValidate.push("freezerModel");
      fieldsToValidate.push("freezerModelId");
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    // Duplicate check: vehicleModel + vehicleType must be unique
    const isDuplicate = fields.some(
      (item, index) =>
        item.vehicleModel.toLowerCase() ===
          values.vehicleModel.toLowerCase() &&
        item.vehicleType === values.vehicleType &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("vehicleModel", {
        type: "validate",
        message: t("error-duplicate-exists"),
      });
      return;
    }

    const newItem: VehicleModelItem = {
      vehicleModel: values.vehicleModel,
      vehicleCategory: values.vehicleCategory,
      vehicleType: values.vehicleType,
      vehicleTypeId: values.vehicleTypeId,
      freezerModel: isFreezerTruck ? values.freezerModel : "",
      freezerModelId: isFreezerTruck ? values.freezerModelId : "",
      tyreCount: Number(values.tyreCount),
      tyreSize: values.tyreSize || "",
      axleCount: Number(values.axleCount),
      tollClass: values.tollClass,
      tollClassId: values.tollClassId,
      fuelType: isTruck ? values.fuelType : "",
      fuelTypeId: isTruck ? values.fuelTypeId : "",
      fuelCapacity: values.fuelCapacity ? parseFormattedNumber(values.fuelCapacity) : null,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    // Reset form fields
    setValue("vehicleModel", "");
    setValue("vehicleType", "");
    setValue("vehicleTypeId", "");
    setValue("freezerModel", "");
    setValue("freezerModelId", "");
    setValue("tyreCount", "");
    setValue("tyreSize", "");
    setValue("axleCount", "");
    setValue("tollClass", "");
    setValue("tollClassId", "");
    setValue("fuelType", "");
    setValue("fuelTypeId", "");
    setValue("fuelCapacity", undefined as any);
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("vehicleModel", item.vehicleModel);
    setValue("vehicleCategory", item.vehicleCategory);
    setValue("vehicleType", item.vehicleType);
    setValue("vehicleTypeId", item.vehicleTypeId);
    setValue("freezerModel", item.freezerModel);
    setValue("freezerModelId", item.freezerModelId);
    setValue("tyreCount", String(item.tyreCount));
    setValue("tyreSize", item.tyreSize || "");
    setValue("axleCount", String(item.axleCount));
    setValue("tollClass", item.tollClass);
    setValue("tollClassId", item.tollClassId);
    setValue("fuelType", item.fuelType);
    setValue("fuelTypeId", item.fuelTypeId);
    setValue("fuelCapacity", item.fuelCapacity !== null ? String(item.fuelCapacity) : "");
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormFields();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetFormFields();
  };

  const resetFormFields = () => {
    setValue("vehicleModel", "");
    setValue("vehicleType", "");
    setValue("vehicleTypeId", "");
    setValue("freezerModel", "");
    setValue("freezerModelId", "");
    setValue("tyreCount", "");
    setValue("tyreSize", "");
    setValue("axleCount", "");
    setValue("tollClass", "");
    setValue("tollClassId", "");
    setValue("fuelType", "");
    setValue("fuelTypeId", "");
    setValue("fuelCapacity", undefined as any);
    setValue("status", "active");
  };

  const handleSave = async () => {
    const payload = fields.map((item) => ({
      vehicle_model: item.vehicleModel,
      vehicle_type_id: item.vehicleTypeId,
      tyre_count: item.tyreCount,
      axle_count: item.axleCount,
      toll_class_id: item.tollClassId,
      fuel_type_id: item.fuelTypeId,
      status: item.status,
      ...(item.freezerModelId ? { freezer_model_id: item.freezerModelId } : {}),
      ...(item.tyreSize ? { tyre_size: item.tyreSize } : {}),
      ...(item.fuelCapacity !== null ? { fuel_capacity: item.fuelCapacity ?? undefined } : {}),
    }));
    try {
      await batchCreateVehicleModel({
        vehicleModelV1BatchCreateVehicleModelReq: { items: payload },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    isTruck,
    isFreezerTruck,
    watchedCategory,
    watchedVehicleType,
    vehicleTypeOptions,
    tollClassOptions,
    freezerModelOptions,
    fuelTypeOptions,
  };
};

export default useAddVehicleModelForm;
